import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { User } from '@shared/types';
import LogsViewer from './LogsViewer';
import { AdminProcesses } from './AdminProcesses';

type Tab = 'users' | 'logs' | 'processes';

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState<Tab>('users');
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [approvedUsers, setApprovedUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingUserId, setApprovingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    console.log('[AdminPanel] Loading users via API...');
    setLoading(true);
    setError(null);

    try {
      console.log('[AdminPanel] Calling /api/admin/users...');
      const response: any = await api.get('/admin/users');
      console.log('[AdminPanel] API response:', response);

      const users = response.data?.users || response.users || [];

      const pending = users.filter((u: User) => !u.is_approved) || [];
      const approved = users.filter((u: User) => u.is_approved) || [];

      console.log('[AdminPanel] Users loaded:', { pending: pending.length, approved: approved.length });

      setPendingUsers(pending);
      setApprovedUsers(approved);
    } catch (error: any) {
      console.error('[AdminPanel] Failed to load users:', error);
      setError(error.message || 'Failed to load users');
      toast.error('Failed to load users: ' + (error.message || 'Unknown error'));
    } finally {
      console.log('[AdminPanel] Setting loading to false');
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    setApprovingUserId(userId);
    try {
      // Step 1: Provision container for user
      toast.info('Provisioning isolated container...');
      await api.post(`/containers/provision/${userId}`);

      // Step 2: Approve user in database via API
      await api.post(`/admin/users/${userId}/approve`);

      toast.success('User approved and container provisioned');
      loadUsers();
    } catch (error: any) {
      console.error('Failed to approve user:', error);
      toast.error(error.message || 'Failed to approve user');
    } finally {
      setApprovingUserId(null);
    }
  };

  const handleReject = async (userId: string) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User rejected');
      loadUsers();
    } catch (error: any) {
      console.error('Failed to reject user:', error);
      toast.error('Failed to reject user: ' + (error.message || 'Unknown error'));
    }
  };

  const handleToggleSuperUser = async (userId: string, currentStatus: boolean) => {
    try {
      await api.put(`/admin/users/${userId}/super-user`, {
        is_super_user: !currentStatus
      });
      toast.success(currentStatus ? 'Super user removed' : 'Super user granted');
      loadUsers();
    } catch (error: any) {
      console.error('Failed to update super user status:', error);
      toast.error('Failed to update super user status: ' + (error.message || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-muted-foreground">Loading admin panel...</div>
        {error && (
          <div className="text-red-500 text-sm">Error: {error}</div>
        )}
        <button
          onClick={loadUsers}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-red-500">Failed to load admin panel</div>
        <div className="text-sm text-muted-foreground">{error}</div>
        <button
          onClick={loadUsers}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="border-b">
        <div className="flex gap-1 p-2">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'users'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'logs'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted'
            }`}
          >
            Server Logs
          </button>
          <button
            onClick={() => setActiveTab('processes')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'processes' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
          >
            Processes
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {activeTab === 'users' && (
          <div className="h-full overflow-auto p-6">
            <h1 className="text-2xl font-bold mb-6">User Management v2.0</h1>

            <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <h3 className="font-semibold text-blue-400 mb-2">🔒 Multi-Tenant Isolation Enabled</h3>
              <p className="text-sm text-muted-foreground">
                Each user gets an isolated Docker container with resource limits and no network access.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">
                Pending Approval ({pendingUsers.length})
              </h2>
              {pendingUsers.length === 0 ? (
                <div className="text-muted-foreground text-sm">No pending users</div>
              ) : (
                <div className="space-y-2">
                  {pendingUsers.map((user) => (
                    <div
                      key={user.id}
                      className="border rounded-lg p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        {user.avatar_url && (
                          <img
                            src={user.avatar_url}
                            alt={user.email}
                            className="w-10 h-10 rounded-full"
                          />
                        )}
                        <div>
                          <div className="font-medium">{user.full_name || user.email}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                          <div className="text-xs text-muted-foreground">
                            Registered: {new Date(user.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(user.id)}
                          disabled={approvingUserId === user.id}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {approvingUserId === user.id ? 'Provisioning...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(user.id)}
                          disabled={approvingUserId === user.id}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-4">
                Approved Users ({approvedUsers.length})
              </h2>
              <div className="space-y-2">
                {approvedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="border rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      {user.avatar_url && (
                        <img
                          src={user.avatar_url}
                          alt={user.email}
                          className="w-10 h-10 rounded-full"
                        />
                      )}
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {user.full_name || user.email}
                          {user.is_super_user && (
                            <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded">
                              SUPER USER
                            </span>
                          )}
                          {user.container_id && (
                            <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded">
                              🐳 ISOLATED
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleToggleSuperUser(user.id, user.is_super_user)}
                        className={user.is_super_user ? 'px-4 py-2 bg-gray-600 text-white hover:bg-gray-700 rounded-md transition-colors text-sm' : 'px-4 py-2 bg-purple-600 text-white hover:bg-purple-700 rounded-md transition-colors text-sm'}
                      >
                        {user.is_super_user ? 'Remove Super User' : 'Make Super User'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'logs' && <LogsViewer />}
        {activeTab === 'processes' && <AdminProcesses />}
      </div>
    </div>
  );
}
